import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import SalesDashboard from './SalesDashboard';
import ReservationDashboard from './ReservationDashboard';
import AccountsDashboard from './AccountsDashboard';
import OtherServicesDashboard from './OtherServicesDashboard';
import SuperAdminDashboard from './SuperAdminDashboard';

const UnifiedDashboard = () => {
  const { user: authUser, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [user, setUser] = useState(null);

  useEffect(() => {
    // Check for employee data - try both localStorage keys for compatibility
    const employeeData = localStorage.getItem('employeeData') || localStorage.getItem('employeeInfo');
    const employeeToken = localStorage.getItem('employeeToken');

    if (employeeToken && employeeData) {
      try {
        const parsedEmployee = JSON.parse(employeeData);
        parsedEmployee.userType = 'employee';
        console.log('👤 Loading employee from localStorage:', parsedEmployee);
        console.log('🏢 Employee Team Type:', parsedEmployee.teamType);
        setUser(parsedEmployee);
      } catch (error) {
        console.error('Error parsing employee data:', error);
      }
    } else if (authUser) {
      // Fallback to AuthContext user
      setUser(authUser);
    }

    setLoading(false);
  }, [authUser]);

  useEffect(() => {
    if (user) {
      // Check if user is trying to access unauthorized dashboard
      checkAccess();
    }
  }, [user, location.pathname]);

  // Function to check if user has access to current route
  const checkAccess = () => {
    const currentPath = location.pathname;
    const userTeam = user?.teamType || user?.team || user?.department;
    const userType = user?.userType;
    const userRole = user?.role;

    console.log('🔒 Access Control Check:', {
      currentPath,
      userTeam,
      userType,
      userRole
    });

    // Define allowed paths for each user type
    const allowedPaths = {
      superadmin: ['/dashboard', '/admin', '/superadmin'],
      sales: ['/dashboard', '/sales'],
      reservation: ['/dashboard', '/reservation'],
      accounts: ['/dashboard', '/accounts'],
      other_services: ['/dashboard', '/other-services']
    };

    // Check if user is trying to access a specific dashboard route
    let hasAccess = true;

    // Super Admin has access to everything
    if (userType === 'superadmin' || userRole === 'Admin') {
      hasAccess = true;
    }
    // Check team-based access
    else if (userTeam) {
      // If trying to access a specific team dashboard
      if (currentPath.includes('/sales') && userTeam !== 'sales') {
        hasAccess = false;
      } else if (currentPath.includes('/reservation') && userTeam !== 'reservation') {
        hasAccess = false;
      } else if (currentPath.includes('/accounts') && userTeam !== 'accounts') {
        hasAccess = false;
      } else if (currentPath.includes('/other-services') && userTeam !== 'other_services') {
        hasAccess = false;
      }
    }

    if (!hasAccess) {
      console.log('❌ Access Denied - User does not have permission for this dashboard');
      setAccessDenied(true);
    } else {
      console.log('✅ Access Granted');
      setAccessDenied(false);
    }
  };

  const handleLogout = () => {
    logout();
    window.location.href = '/login';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // Show access denied screen if user doesn't have permission
  if (accessDenied) {
    const userTeam = user?.teamType || user?.team || user?.department;
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-orange-50">
        <div className="text-center bg-white p-8 rounded-lg shadow-xl max-w-md">
          <div className="mb-6">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Access Denied</h2>
            <p className="text-gray-600 mb-4">
              You don't have permission to access this dashboard.
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-blue-800">
                <strong>Your Access:</strong> {userTeam ? `${userTeam.charAt(0).toUpperCase() + userTeam.slice(1)} Dashboard` : 'Limited Access'}
              </p>
            </div>
            <p className="text-sm text-gray-500 mb-6">
              You can only access the dashboard assigned to your team.
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex-1 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-all font-medium"
            >
              Go to My Dashboard
            </button>
            <button
              onClick={handleLogout}
              className="flex-1 bg-gray-600 text-white px-6 py-3 rounded-lg hover:bg-gray-700 transition-all font-medium"
            >
              Logout
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Determine which dashboard to show based on user's team
  const getDashboard = () => {
    console.log('🎯 UnifiedDashboard - User data:', user);
    console.log('🎯 User Type:', user?.userType);
    console.log('🎯 Team Type:', user?.teamType);
    console.log('🎯 Role:', user?.role);

    // Super Admin - Full Access
    if (user.userType === 'superadmin' || user.role === 'Admin') {
      console.log('✅ Showing SuperAdminDashboard (Full Access)');
      return <SuperAdminDashboard />;
    }

    // Team-based dashboards for employees - STRICT ACCESS CONTROL
    if (user.userType === 'employee') {
      const teamType = user.teamType || user.team || user.department;

      // Sales Team - ONLY Sales Dashboard
      if (teamType === 'sales') {
        console.log('✅ Showing SalesDashboard (Sales Team Only)');
        return <SalesDashboard />;
      }

      // Reservation Team - ONLY Reservation Dashboard
      if (teamType === 'reservation') {
        console.log('✅ Showing ReservationDashboard (Reservation Team Only)');
        return <ReservationDashboard />;
      }

      // Other Services Team - ONLY Other Services Dashboard
      if (teamType === 'other_services') {
        console.log('✅ Showing OtherServicesDashboard (Other Services Team Only)');
        return <OtherServicesDashboard />;
      }

      // Accounts Team - ONLY Accounts Dashboard
      if (teamType === 'accounts') {
        console.log('✅ Showing AccountsDashboard (Accounts Team Only)');
        return <AccountsDashboard />;
      }

      // No valid team found - Show error
      console.warn('⚠️ No valid teamType found for employee');
      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-yellow-50 to-orange-50">
          <div className="text-center bg-white p-8 rounded-lg shadow-xl max-w-md">
            <div className="mb-6">
              <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-10 h-10 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Team Not Assigned</h2>
              <p className="text-gray-600 mb-4">
                Your account is not assigned to any team.
              </p>
              <p className="text-sm text-gray-500 mb-6">
                Please contact your administrator to assign you to a team.
              </p>
            </div>
            <button
              onClick={handleLogout}
              className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-all font-medium"
            >
              Logout
            </button>
          </div>
        </div>
      );
    }

    // Invalid user type - Show error
    console.warn('⚠️ No valid userType found');
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-pink-50">
        <div className="text-center bg-white p-8 rounded-lg shadow-xl max-w-md">
          <div className="mb-6">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Invalid Account</h2>
            <p className="text-gray-600 mb-4">
              Your account type is not recognized.
            </p>
            <p className="text-sm text-gray-500 mb-6">
              Please contact your administrator for assistance.
            </p>
          </div>
          <button
            onClick={handleLogout}
            className="w-full bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 transition-all font-medium"
          >
            Logout
          </button>
        </div>
      </div>
    );
  };

  // Check if dashboard has its own sidebar (don't show top nav)
  const hasSidebar = user?.teamType === 'reservation' || user?.teamType === 'sales' ||
    user?.teamType === 'accounts' || user?.teamType === 'other_services' ||
    user?.userType === 'superadmin' || user?.role === 'Admin';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Navigation Bar - Only show for dashboards without sidebar */}
      {!hasSidebar && (
        <nav className="bg-white shadow-sm border-b">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm text-gray-600">
                  {user.teamType ? `${user.teamType.charAt(0).toUpperCase() + user.teamType.slice(1)} Team` : 'Dashboard'}
                </p>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <p className="font-medium text-gray-800">{user.teamMemberName || user.name}</p>
                  <p className="text-sm text-gray-600">{user.email}</p>
                </div>
                <button
                  onClick={handleLogout}
                  className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700"
                >
                  Logout
                </button>
              </div>
            </div>
          </div>
        </nav>
      )}

      {/* Dashboard Content */}
      {getDashboard()}
    </div>
  );
};

export default UnifiedDashboard;
